/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { SearchOutlined } from '@ant-design/icons';
import { Input as AntdInput, Modal } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';

import { Node } from '@/client';
import { PaginatedTable } from '@/components/PaginatedTable';
import { QueryWrapper } from '@/components/QueryWrapper';
import { TagsList } from '@/components/TagsList';
import { usePaginatedNodesQuery } from '@/hooks/query/usePaginatedNodesQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { TableProps } from '@/primitives/Table';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

type SelectNodesModalProps = {
  open: boolean;
  initialSelectedNodes: Node[];
  onClose: () => void;
  onConfirm: (nodes: Node[]) => void;
};

export function SelectNodesModal({ open, initialSelectedNodes, onClose, onConfirm }: SelectNodesModalProps) {
  const { t } = useMimicTranslation('notificationManagement');
  const [selectedNodesMap, setSelectedNodesMap] = useState<Map<string, Node>>(new Map());
  const [searchText, setSearchText] = useState('');

  const nodesQuery = usePaginatedNodesQuery({
    params: {
      filters: searchText ? { name: searchText } : {},
      page: 1,
      pageSize: 10,
    },
    type: 'inMemory',
  });

  useEffect(() => {
    if (open) {
      // Initialize selection with the nodes that were already selected
      const initialMap = new Map<string, Node>();

      initialSelectedNodes.forEach((node) => {
        initialMap.set(node.id, node);
      });

      setSelectedNodesMap(initialMap);
    }
  }, [open, initialSelectedNodes]);

  const selectedKeys = Array.from(selectedNodesMap.keys());

  const columns: ColumnsType<Node> = [
    {
      title: t('selectNodes.hostName'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <Text type="link" size="md">
          {name}
        </Text>
      ),
    },
    {
      title: t('selectNodes.nodeId'),
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => (
        <Text size="sm" mono>
          {id}
        </Text>
      ),
    },
    {
      title: t('selectNodes.nodeTags'),
      dataIndex: 'tags',
      key: 'tags',
      render: (tags: string[]) => {
        return <TagsList tags={tags} />;
      },
    },
  ];

  const rowSelection: TableProps<Node>['rowSelection'] = {
    type: 'checkbox',
    preserveSelectedRowKeys: true,
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[], rows: Node[]) => {
      // Maintain a map of selected nodes to preserve data across pages
      const newMap = new Map(selectedNodesMap);

      // Add newly selected nodes
      rows.forEach((node) => {
        newMap.set(node.id, node);
      });

      // Remove deselected nodes
      const keysSet = new Set(keys);

      selectedNodesMap.forEach((_, nodeId) => {
        if (!keysSet.has(nodeId)) {
          newMap.delete(nodeId);
        }
      });

      setSelectedNodesMap(newMap);
    },
    getCheckboxProps: (node: Node) => ({
      name: node.name,
    }),
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedNodesMap.values()));
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={800}
      closeIcon={null}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button type="default" onClick={onClose} dataTestId="cancel-select-nodes">
            {t('cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={selectedKeys.length === 0}
            dataTestId="confirm-select-nodes"
          >
            {t('selectNodes.addNodes', { count: selectedKeys.length })}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <Title level={4}>{t('selectNodes.title')}</Title>
          <Text type="subtle">{t('selectNodes.description')}</Text>
        </div>
        <AntdInput
          placeholder={t('selectNodes.searchPlaceholder')}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 240 }}
          data-testid="search-nodes-input"
        />
      </div>

      <div style={{ maxHeight: '400px', overflow: 'auto' }}>
        <QueryWrapper queryResult={nodesQuery}>
          {(nodesData) => (
            <PaginatedTable
              data={nodesData}
              columns={columns}
              rowKey="id"
              rowSelection={rowSelection}
              onParamsChange={nodesQuery.updateQueryParams}
              hidePaginationInfo
              data-testid="select-nodes-table"
            />
          )}
        </QueryWrapper>
      </div>
    </Modal>
  );
}
